/**
 * Image Cache Service
 * Client-side localStorage caching for generated images
 *
 * For internal testing until blob storage with ACL is implemented.
 * Stores full-resolution base64 images in browser localStorage.
 */

type CachedImage = {
  data: string // Base64 data URL
  timestamp: number // When cached
}

type ImageCache = {
  [jobId: string]: CachedImage
}

export class ImageCacheService {
  private static readonly STORAGE_KEY = "studio-ai-image-cache"
  // The budget is metered in UTF-16 code units (one per data URL character),
  // the unit browsers meter localStorage in; the origin's whole quota is about
  // 5 M units shared with every other key, so this stays well below it.
  private static readonly MAX_CACHE_UNITS = 2 * 1024 * 1024
  private static readonly MAX_AGE_HOURS = 24
  /** Estimated JSON overhead for the outer `{}` wrapper and commas, in units. */
  private static readonly JSON_OVERHEAD_UNITS = 100

  /** Estimated size of a cache entry in units, without JSON.stringify + Blob overhead. */
  private static estimateEntryUnits(jobId: string, entry: CachedImage): number {
    // JSON overhead per entry: key + data string + timestamp number + delimiters (~50 units)
    return jobId.length + entry.data.length + 50
  }

  /**
   * Store image with job_id as key
   * @param jobId Job identifier
   * @param imageDataUrl Base64 data URL
   * @returns true if cached successfully, false if the image does not fit
   */
  static setImage(jobId: string, imageDataUrl: string): boolean {
    try {
      const cache = this.getCache()
      const timestamp = Date.now()
      const newEntry: CachedImage = { data: imageDataUrl, timestamp }

      const maxUnits = this.MAX_CACHE_UNITS
      // An image the cache cannot hold on its own is refused up front: evicting
      // every other entry would not make room, and reporting success for an
      // entry that was itself evicted misled callers into skipping the stored
      // session file.
      if (this.JSON_OVERHEAD_UNITS + this.estimateEntryUnits(jobId, newEntry) > maxUnits) {
        return false
      }

      // Compute current estimated size incrementally (avoids re-serializing entire cache)
      delete cache[jobId]
      let estimatedUnits = this.JSON_OVERHEAD_UNITS
      for (const [key, entry] of Object.entries(cache)) {
        estimatedUnits += this.estimateEntryUnits(key, entry)
      }
      estimatedUnits += this.estimateEntryUnits(jobId, newEntry)

      if (estimatedUnits > maxUnits) {
        // Remove oldest entries until under limit (never the one being added)
        const entries = Object.entries(cache).sort(([, a], [, b]) => a.timestamp - b.timestamp)

        while (entries.length > 0 && estimatedUnits > maxUnits) {
          const [oldestKey, oldestEntry] = entries.shift()!
          estimatedUnits -= this.estimateEntryUnits(oldestKey, oldestEntry)
          delete cache[oldestKey]
        }
      }

      // Add new image
      cache[jobId] = newEntry

      // Save to localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cache))
      return true
    } catch (error) {
      if (error instanceof Error && error.name === "QuotaExceededError") {
        // Evict oldest entries and retry once
        try {
          const fallbackCache = this.getCache()
          const entries = Object.entries(fallbackCache).sort(
            ([, a], [, b]) => a.timestamp - b.timestamp
          )
          // Remove half of entries to free space
          const removeCount = Math.max(1, Math.floor(entries.length / 2))
          for (let i = 0; i < removeCount && i < entries.length; i++) {
            delete fallbackCache[entries[i][0]]
          }
          fallbackCache[jobId] = { data: imageDataUrl, timestamp: Date.now() }
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(fallbackCache))
          return true
        } catch {
          // Still failing — clear everything and give up
          localStorage.removeItem(this.STORAGE_KEY)
          return false
        }
      }
      // eslint-disable-next-line no-console
      console.error("Failed to cache image:", error)
      return false
    }
  }

  /**
   * Retrieve image by job_id
   * @param jobId Job identifier
   * @returns Base64 data URL or null if not found/expired
   */
  static getImage(jobId: string): string | null {
    try {
      const cache = this.getCache()

      // Check if job exists in cache
      if (!(jobId in cache)) {
        return null
      }

      const cached = cache[jobId]

      // Validate cached entry structure
      if (!cached.data || typeof cached.timestamp !== "number") {
        return null
      }

      // Check if expired
      const ageInHours = (Date.now() - cached.timestamp) / (1000 * 60 * 60)
      if (ageInHours > this.MAX_AGE_HOURS) {
        // Remove expired entry
        delete cache[jobId]
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cache))
        return null
      }

      return cached.data
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to retrieve cached image:", error)
      return null
    }
  }

  /**
   * Retrieve several images with a single localStorage parse.
   * @param jobIds Job identifiers
   * @returns Map of non-expired cached data URLs by job id
   */
  static getImages(jobIds: string[]): Map<string, string> {
    const results = new Map<string, string>()
    if (jobIds.length === 0) return results

    try {
      const cache = this.getCache()
      const cacheLookup: Partial<ImageCache> = cache
      const now = Date.now()
      const maxAgeMs = this.MAX_AGE_HOURS * 60 * 60 * 1000
      let removedCount = 0

      for (const jobId of jobIds) {
        const cached = cacheLookup[jobId]
        if (!cached) continue
        if (!cached.data || typeof cached.timestamp !== "number") {
          delete cache[jobId]
          removedCount++
          continue
        }
        if (now - cached.timestamp > maxAgeMs) {
          delete cache[jobId]
          removedCount++
          continue
        }
        results.set(jobId, cached.data)
      }

      if (removedCount > 0) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cache))
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to retrieve cached images:", error)
    }

    return results
  }

  /**
   * Remove specific image from cache
   * @param jobId Job identifier
   */
  static removeImage(jobId: string): void {
    try {
      const cache = this.getCache()
      delete cache[jobId]
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cache))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to remove cached image:", error)
    }
  }

  /**
   * Clear expired images (older than MAX_AGE_HOURS)
   * @returns Number of images removed
   */
  static cleanupExpired(): number {
    try {
      const cache = this.getCache()
      const now = Date.now()
      const maxAgeMs = this.MAX_AGE_HOURS * 60 * 60 * 1000
      let removedCount = 0

      Object.keys(cache).forEach((jobId) => {
        const cached = cache[jobId]
        // Validate entry has timestamp
        if (typeof cached.timestamp === "number") {
          if (now - cached.timestamp > maxAgeMs) {
            // Remove expired entries
            delete cache[jobId]
            removedCount++
          }
        } else {
          // Remove invalid entries (missing or malformed timestamp)
          delete cache[jobId]
          removedCount++
        }
      })

      if (removedCount > 0) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cache))
        // eslint-disable-next-line no-console
        console.log(`Cleaned up ${removedCount} expired cached images`)
      }

      return removedCount
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to cleanup expired images:", error)
      return 0
    }
  }

  /**
   * Clear all cached images
   */
  static clearAll(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
      // eslint-disable-next-line no-console
      console.log("Cleared all cached images")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to clear cache:", error)
    }
  }

  /**
   * Get cache size in MB (millions of units, the same measure as the budget)
   * @returns Size in megabytes
   */
  static getCacheSize(): number {
    try {
      const cacheString = localStorage.getItem(this.STORAGE_KEY)
      if (!cacheString) {
        return 0
      }
      return cacheString.length / (1024 * 1024)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to get cache size:", error)
      return 0
    }
  }

  /**
   * Check if cache is near limit (>80%)
   * @returns true if cache is near limit
   */
  static isNearLimit(): boolean {
    return this.getCacheSize() > (this.MAX_CACHE_UNITS / (1024 * 1024)) * 0.8
  }

  /**
   * Get count of cached images
   * @returns Number of cached images
   */
  static getCount(): number {
    try {
      const cache = this.getCache()
      return Object.keys(cache).length
    } catch {
      return 0
    }
  }

  /**
   * Get all cache data (for debugging)
   * @returns Cache object
   */
  private static getCache(): ImageCache {
    try {
      const cacheString = localStorage.getItem(this.STORAGE_KEY)
      if (!cacheString) {
        return {}
      }
      return JSON.parse(cacheString) as ImageCache
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to parse cache, resetting:", error)
      localStorage.removeItem(this.STORAGE_KEY)
      return {}
    }
  }
}
